import React, { useState, useEffect } from 'react';
import { Button, Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Blueprint } from '../../types/blog-generation-agent.types';

interface GenerateBlogFromBlueprintDialogProps {
  open: boolean;
  onClose: () => void;
  blueprint: Blueprint;
  parentRunId: string;
  onGenerate: (blueprint: Blueprint, suggestion?: string) => void;
}

export const GenerateBlogFromBlueprintDialog: React.FC<
  GenerateBlogFromBlueprintDialogProps
> = ({ open, onClose, blueprint, parentRunId, onGenerate }) => {
  const [suggestion, setSuggestion] = useState('');

  useEffect(() => {
    setSuggestion('');
  }, [blueprint?._id]);

  const handleGenerate = () => {
    onGenerate(blueprint, suggestion.trim() || undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} disableScrollLock>
      <div className="p-6 min-w-[600px] max-w-[600px] relative">
        <p className="text-primary font-semibold text-xl mb-4">
          Generate Blog Post
        </p>

        {/* Blueprint Context Box */}
        <div className="mb-4">
          <p className="text-[#475467] text-sm font-medium mb-2">
            {blueprint.title}
          </p>
          {/* Keyword labels */}
          <div className="flex items-center gap-1 flex-wrap mb-2">
            {blueprint.primary_keywords?.map((keyword, idx) => (
              <span
                key={idx}
                className="text-xs font-medium px-2 py-0.5 rounded-2xl bg-[#ECFDF3] text-[#1A927B]"
              >
                {keyword}
              </span>
            ))}
            {blueprint.secondary_keywords?.map((keyword, idx) => (
              <span
                key={idx}
                className="text-xs font-medium px-2 py-0.5 rounded-2xl bg-[#F5F5F5] text-[#414651]"
              >
                {keyword}
              </span>
            ))}
          </div>
          <p className="text-[#475467] text-xs font-medium">
            {blueprint.synopsis}
          </p>
        </div>

        {/* Suggestion input */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-[#344054] mb-1.5">
            Any suggestions for this blog post?{' '}
            <span className="text-[#667085] font-normal">(Optional)</span>
          </label>
          <textarea
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            placeholder="e.g., Focus more on the benefits of keyword tracking, write in a conversational tone, or specify key headers..."
            className="w-full h-24 p-3 text-sm bg-white border border-[#D0D5DD] rounded-lg focus:outline-none focus:border-[var(--color-text-purple)] focus:ring-1 focus:ring-[var(--color-text-purple)] transition-all duration-200 resize-none placeholder-[#98A2B3]"
          />
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            className="h-11 flex-1"
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: '#D0D5DD',
              color: '#344054',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              '&:hover': {
                borderColor: '#D0D5DD',
                backgroundColor: 'transparent'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            className="h-11 flex-1 gap-2"
            variant="contained"
            onClick={handleGenerate}
            sx={{
              backgroundColor: '#0F0A3C',
              color: '#FFFFFF',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              '&:hover': {
                backgroundColor: '#0F0A3C'
              }
            }}
          >
            Generate Post
          </Button>
        </div>

        {/* Top-right close button */}
        <div aria-label="dialog-close-btn" className="absolute top-2 right-2">
          <IconButton
            aria-label="close"
            onClick={onClose}
            className="hover:rounded-lg"
            sx={{
              height: '40px',
              padding: '8px'
            }}
          >
            <CloseIcon sx={{ color: '#667085' }} />
          </IconButton>
        </div>
      </div>
    </Dialog>
  );
};
